import { ChevronRight } from 'lucide-react'
import { useQueryState } from 'nuqs'
import { ReactNode, useMemo } from 'react'
import { Link } from 'react-router'

import { Money, PageHeader, SegmentedTabs, StatStrip } from '@/components/ds'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { DashboardStats } from '@/interfaces/DashboardStats'

import {
  buildMonthRows,
  monthWindow,
  Period,
  periodOptions,
  periodStart,
  periodTotals,
  Ranked,
  rankCounterparties,
  useDocsForRanking,
  useMonthlyReceipts,
} from './dashboardData'
import { ChartMode, MonthlyGraph } from './MonthlyGraph'
import { formatMonthLong } from './number-format'
import { YearlyGraph } from './YearlyGraph'

const periodDescription: Record<Period, string> = {
  month: 'Tento měsíc',
  '30d': 'Posledních 30 dní',
  year: `Rok ${new Date().getFullYear()}`,
  '12m': 'Posledních 12 měsíců',
}

const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many)

export const Dashboard = ({ data, currency = 'CZK' }: { data: DashboardStats; currency?: string }) => {
  const [tabParam, setTab] = useQueryState('tab')
  const [periodParam, setPeriod] = useQueryState('period')
  const tab = (['overview', 'income', 'expenses'].includes(tabParam ?? '') ? tabParam : 'overview') as ChartMode
  const period = (periodOptions.some((p) => p.value === periodParam) ? periodParam : '12m') as Period

  const months = useMemo(() => monthWindow(), [])
  const { receipts, isLoading: receiptsLoading } = useMonthlyReceipts(months)
  const hasReceipts = !!receipts?.some((v) => v !== 0)
  const allRows = useMemo(() => buildMonthRows(data, months, receipts), [data, months, receipts])
  const rows = useMemo(() => {
    const all = allRows
    // backend počítá okno od data před rokem, první měsíc tak bývá prázdný; prázdný úvodní měsíc vynecháme
    const first = all[0]
    const empty = first && !first.paid && !first.unpaid && !first.expenses && !first.receipts
    return empty ? all.slice(1) : all
  }, [allRows])
  const totals = periodTotals(data, allRows, period, hasReceipts)

  const invoices = useDocsForRanking('invoices')
  const expenses = useDocsForRanking('expenses')
  const fromTime = periodStart(period).getTime()
  const customers = useMemo(() => rankCounterparties(invoices.docs, 'customer', new Date(fromTime), currency), [invoices.docs, fromTime, currency])
  const suppliers = useMemo(() => rankCounterparties(expenses.docs, 'supplier', new Date(fromTime), currency), [expenses.docs, fromTime, currency])

  const setTabValue = (v: ChartMode) => setTab(v === 'overview' ? null : v)

  const incomeHint = (
    <>
      <span className="block">
        Zaplacené faktury <Money amount={totals.paid} currency={currency} size="sm" className="font-normal text-foreground" /> z{' '}
        <Money amount={totals.issued} currency={currency} size="sm" className="font-normal" />
      </span>
      {hasReceipts && (
        <span className="block">
          {totals.receipts === null ? (
            'Bez tržeb z pokladny, za 30 dní je neumíme spočítat'
          ) : (
            <>
              Pokladna <Money amount={totals.receipts} currency={currency} size="sm" className="font-normal text-foreground" />
            </>
          )}
        </span>
      )}
    </>
  )

  const expensesHint =
    totals.expensesPaid !== null ? (
      <>
        Zaplaceno <Money amount={totals.expensesPaid} currency={currency} size="sm" className="font-normal text-foreground" />
      </>
    ) : (
      'Přijaté doklady podle data vystavení'
    )

  return (
    <div className="mx-auto w-full max-w-[1280px]">
      <PageHeader
        title="Přehled"
        description={`${periodDescription[period]}, částky bez DPH`}
        actions={
          <Select value={period} onValueChange={(v) => setPeriod(v === '12m' ? null : v)}>
            <SelectTrigger className="w-40" aria-label="Období">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {periodOptions.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SegmentedTabs<ChartMode>
        className="mb-4"
        value={tab}
        onChange={setTabValue}
        options={[
          { value: 'overview', label: 'Přehled' },
          { value: 'income', label: 'Příjmy' },
          { value: 'expenses', label: 'Výdaje' },
        ]}
      />

      <StatStrip
        className="mb-6"
        items={[
          {
            label: 'Čistý cashflow',
            value: receiptsLoading ? <Skeleton className="h-8 w-40" /> : <Money amount={totals.net} currency={currency} size="lg" animate signed tone />,
            hint: 'Příjmy minus výdaje',
            active: tab === 'overview',
            onClick: () => setTabValue('overview'),
          },
          {
            label: hasReceipts ? 'Příjmy (faktury a pokladna)' : 'Příjmy',
            value: receiptsLoading ? <Skeleton className="h-8 w-40" /> : <Money amount={totals.income} currency={currency} size="lg" animate />,
            hint: incomeHint,
            active: tab === 'income',
            onClick: () => setTabValue('income'),
          },
          {
            label: 'Výdaje',
            value: <Money amount={totals.expenses} currency={currency} size="lg" animate />,
            hint: expensesHint,
            active: tab === 'expenses',
            onClick: () => setTabValue('expenses'),
          },
        ]}
      />

      <section className="mb-6 rounded-lg border px-5 pt-4 pb-3">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium">
            {tab === 'overview' ? 'Příjmy a náklady po měsících' : tab === 'income' ? 'Příjmy po měsících' : 'Náklady po měsících'}
          </h2>
          <p className="text-xs text-muted-foreground">Po měsících podle data vystavení</p>
        </div>
        <MonthlyGraph rows={rows} mode={tab} hasReceipts={hasReceipts} />
        {receipts === null && (
          <p className="mt-2 text-xs text-muted-foreground">Tržby z pokladny se nepodařilo načíst, v příjmech nejsou započtené.</p>
        )}
      </section>

      <div className={tab === 'overview' ? 'grid gap-4 lg:grid-cols-3' : 'grid gap-4 lg:grid-cols-[2fr_1fr]'}>
        {tab !== 'expenses' && (
          <Panel title="Top odběratelé" link={{ to: '/invoices', label: 'Faktury' }}>
            <RankList items={customers} limit={tab === 'overview' ? 5 : 10} loading={invoices.isLoading} currency={currency} unit={['faktura', 'faktury', 'faktur']} empty="Za toto období žádné faktury." />
          </Panel>
        )}
        {tab !== 'income' && (
          <Panel title="Největší výdaje podle dodavatele" link={{ to: '/expenses', label: 'Náklady' }}>
            <RankList items={suppliers} limit={tab === 'overview' ? 5 : 10} loading={expenses.isLoading} currency={currency} unit={['doklad', 'doklady', 'dokladů']} empty="Za toto období žádné náklady." />
          </Panel>
        )}
        {tab !== 'expenses' && (
          <Panel title="Vystaveno podle let">
            <YearlyGraph data={data} limit={tab === 'overview' ? 5 : 8} />
          </Panel>
        )}
        {tab === 'expenses' && (
          <Panel title="Náklady po měsících">
            <ul className="divide-y">
              {[...rows].reverse().slice(0, 6).map((r) => (
                <li key={r.date.toISOString()} className="flex items-baseline justify-between py-2.5 text-sm">
                  <span>{formatMonthLong(r.date)}</span>
                  <Money amount={r.expenses} currency={currency} size="sm" />
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    </div>
  )
}

const Panel = ({ title, link, children }: { title: string; link?: { to: string; label: string }; children: ReactNode }) => (
  <section className="rounded-lg border px-5 pt-4 pb-2">
    <div className="mb-1 flex items-baseline justify-between gap-2">
      <h2 className="text-sm font-medium">{title}</h2>
      {link && (
        <Link to={link.to} className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground">
          {link.label}
          <ChevronRight className="size-3.5" />
        </Link>
      )}
    </div>
    {children}
  </section>
)

const RankList = ({
  items,
  limit,
  loading,
  currency,
  unit,
  empty,
}: {
  items: Ranked[]
  limit: number
  loading: boolean
  currency: string
  unit: [string, string, string]
  empty: string
}) => {
  if (loading && !items.length) {
    return (
      <div className="space-y-3 py-2.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    )
  }
  if (!items.length) return <p className="py-6 text-sm text-muted-foreground">{empty}</p>

  const total = items.reduce((s, r) => s + r.total, 0) || 1
  const top = items.slice(0, limit)
  const rest = items.slice(limit)
  const restTotal = rest.reduce((s, r) => s + r.total, 0)

  return (
    <ul className="divide-y">
      {top.map((r) => (
        <li key={r.id ?? r.name} className="py-2.5">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">
              {r.id ? (
                <Link to={`/contact/${r.id}`} className="hover:underline">
                  {r.name}
                </Link>
              ) : (
                r.name
              )}
            </span>
            <Money amount={r.total} currency={currency} size="sm" className="shrink-0" />
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-brand" style={{ width: `${(r.total / total) * 100}%` }} />
            </div>
            <span className="w-28 shrink-0 text-right text-xs text-muted-foreground tabular">
              {Math.round((r.total / total) * 100)} %, {r.count} {plural(r.count, ...unit)}
            </span>
          </div>
        </li>
      ))}
      {rest.length > 0 && (
        <li className="flex items-baseline justify-between py-2.5 text-sm text-muted-foreground">
          <span>
            Ostatní ({rest.length})
          </span>
          <Money amount={restTotal} currency={currency} size="sm" className="font-normal" />
        </li>
      )}
    </ul>
  )
}
